import { useRef, useState } from "react";
import { AnimatePresence, motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, FileText } from "lucide-react";
import { images } from "@/config/content";
import { useLang } from "@/i18n/LanguageContext";
import { CallButton, WhatsAppButton, LocationButton } from "@/components/ActionButtons";
import { ease } from "@/components/Reveal";
import { scrollToTarget } from "@/lib/scroll";

const lineVariants = {
  hidden: { y: "110%" },
  show: (i) => ({ y: "0%", transition: { duration: 0.9, ease, delay: 0.15 + i * 0.12 } }),
};

function MaskedLine({ children, index, className = "" }) {
  return (
    <span className="block overflow-hidden pb-1">
      <motion.span variants={lineVariants} custom={index} initial="hidden" animate="show" className={`block ${className}`}>
        {children}
      </motion.span>
    </span>
  );
}

function scrollToId(id) {
  scrollToTarget(`#${id}`);
}

export function Hero() {
  const { t } = useLang();
  const [mode, setMode] = useState("now"); // "now" | "schedule"
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const overlayOpacity = useTransform(scrollYProgress, [0, 1], [1, 0.4]);

  return (
    <section
      ref={ref}
      id="assistenza"
      data-testid="hero-section"
      className="relative flex min-h-[100svh] items-end overflow-hidden bg-carbon pb-28 pt-28 md:items-center md:pb-20"
    >
      <motion.div style={{ y: bgY }} className="absolute inset-0 z-0 h-[120%]">
        <img
          src={images.heroReal}
          alt="Carroattrezzi EMME SERVICE CAR su strada notturna con luci ambra di sicurezza"
          className="h-full w-full object-cover"
          fetchPriority="high"
        />
      </motion.div>
      <motion.div style={{ opacity: overlayOpacity }} className="absolute inset-0 z-[1] bg-gradient-to-t from-carbon via-carbon/70 to-carbon/40" />
      <div className="absolute inset-0 z-[1] spotlight" />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.6 }}
            className="mb-5 inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.3em] text-amber"
          >
            <span className="h-px w-8 bg-amber" />
            {t.hero.overline}
          </motion.p>

          <h1 className="font-display text-4xl font-extrabold leading-[0.98] tracking-tight text-ice sm:text-5xl lg:text-7xl">
            <MaskedLine index={0}>{t.hero.titleL1}</MaskedLine>
            <MaskedLine index={1} className="text-amber">{t.hero.titleL2}</MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6, duration: 0.6 }}
            className="mt-6 max-w-xl text-lg font-medium leading-relaxed text-ice md:text-xl"
          >
            {t.hero.subtitle}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.75, duration: 0.6 }}
            className="mt-9"
          >
            <p className="mb-3 text-xs font-bold uppercase tracking-[0.24em] text-ice">{t.hero.question}</p>
            <div className="inline-flex flex-col gap-2 rounded-xl border border-edge bg-asphalt/70 p-1.5 backdrop-blur-sm sm:flex-row" role="tablist" aria-label={t.hero.question}>
              {[
                { key: "now", label: t.hero.optNow },
                { key: "schedule", label: t.hero.optSchedule },
              ].map((opt) => (
                <button
                  key={opt.key}
                  role="tab"
                  aria-selected={mode === opt.key}
                  data-testid={`hero-mode-${opt.key}`}
                  onClick={() => setMode(opt.key)}
                  className={`relative rounded-lg px-5 py-3 text-sm font-bold uppercase tracking-wide transition-colors ${
                    mode === opt.key ? "text-carbon" : "text-steel hover:text-ice"
                  }`}
                >
                  {mode === opt.key && (
                    <motion.span layoutId="hero-pill" className="absolute inset-0 -z-0 rounded-lg bg-amber" transition={{ type: "spring", stiffness: 400, damping: 32 }} />
                  )}
                  <span className="relative z-10">{opt.label}</span>
                </button>
              ))}
            </div>

            <div className="mt-6 min-h-[132px]">
              <AnimatePresence mode="wait">
                {mode === "now" ? (
                  <motion.div
                    key="now"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3, ease }}
                  >
                    <div className="flex flex-wrap gap-3">
                      <CallButton size="lg" testId="hero-call-btn" label={t.common.callFull} />
                      <LocationButton size="lg" testId="hero-location-btn" />
                      <WhatsAppButton size="lg" testId="hero-whatsapp-btn" />
                    </div>
                    <p className="mt-4 text-sm text-steel">{t.hero.nowHint}</p>
                  </motion.div>
                ) : (
                  <motion.div
                    key="schedule"
                    initial={{ opacity: 0, y: 12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3, ease }}
                  >
                    <button
                      type="button"
                      onClick={() => scrollToId("preventivo")}
                      data-testid="hero-quote-btn"
                      className="inline-flex items-center justify-center gap-2 rounded-md bg-amber px-7 py-4 font-display text-base font-bold uppercase tracking-wide text-carbon transition-all duration-200 hover:bg-amber/90 active:scale-95"
                    >
                      <FileText className="h-4 w-4" strokeWidth={2.4} />
                      {t.common.requestQuote}
                    </button>
                    <p className="mt-4 max-w-md text-sm text-steel">{t.hero.scheduleHint}</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>
        </div>
      </div>

      <motion.button
        type="button"
        onClick={() => scrollToId("trust")}
        aria-label="Scorri"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.4 }}
        className="absolute bottom-24 left-1/2 hidden -translate-x-1/2 text-steel md:block"
      >
        <ArrowDown className="h-6 w-6 animate-bounce" />
      </motion.button>
    </section>
  );
}
